import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';
import { Sparkles, Anchor, TrendingUp, CalendarRange, Repeat, Gauge, Filter, Globe, Megaphone, Send, ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { useOperatorLocale } from '../../hooks/useOperatorLocale';
import { MonoNum } from './IntelligenceShell';
import { tooltipStyle, BLUES } from './ReportKit';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell } from 'recharts';

export const INSIGHT_META = {
  fleet_expansion: {
    icon: Anchor,
    title: 'Fleet expansion',
    copy: (p, fm) => `${p.assetName} earned ${fm(p.assetRevenue)} in the period. Adding a similar asset could earn ≈ ${'{v}'} at conservative utilization.`,
  },
  pricing_power: {
    icon: TrendingUp,
    title: 'Pricing power',
    copy: (p) => `A ${p.pct}% price lift on ${p.serviceName} ≈ ${'{v}'} extra, if demand holds — worth an A/B test.`,
  },
  repeat_rate: {
    icon: Repeat,
    title: 'Repeat customers',
    copy: (p) => `Your repeat rate is ${p.currentRate}%. Lifting it to ${p.targetRate}% with post-trip offers ≈ ${'{v}'} at your average spend.`,
  },
  load_factor: {
    icon: Gauge,
    title: 'Unfilled seats',
    copy: (p, fm) => `Your fleet runs at ${p.loadFactor}% of capacity. Filling ${p.extraSeats} more seats per trip ≈ ${'{v}'} at ${fm(p.revenuePerGuest)}/guest.`,
  },
  midweek_gap: {
    icon: CalendarRange,
    title: 'Midweek gap',
    copy: (p) => `Weekends carry ${p.weekendShare}% of your trips. Capturing a quarter of that gap midweek ≈ ${'{v}'}.`,
  },
  funnel_recovery: {
    icon: Filter,
    title: 'Funnel recovery',
    copy: (p) => `${p.dropPct}% of booking sessions drop off between "${p.fromLabel}" and "${p.toLabel}". Recovering a quarter of them ≈ ${'{v}'} at your average booking value.`,
  },
  traffic_conversion: {
    icon: Globe,
    title: 'Traffic → bookings',
    copy: (p) => `${(p.visitors || 0).toLocaleString()} people visited your website but only ${p.engagementPct}% opened the booking widget. Pulling 25% more of them into the widget ≈ ${'{v}'}.`,
  },
  audience_activation: {
    icon: Megaphone,
    title: 'Audience activation',
    copy: (p) => `${(p.optedIn || 0).toLocaleString()} contacts opted in to hear from you. A quarterly offer converting just 2% of them ≈ ${'{v}'} at your average booking value.`,
  },
  riders_conversion: {
    icon: Megaphone,
    title: 'Guests → customers',
    copy: (p) => `${(p.riders || 0).toLocaleString()} people signed waivers on someone else's trip but never booked their own. Converting 5% with an intro offer ≈ ${'{v}'}.`,
  },
  campaign_start: {
    icon: Send,
    title: 'Launch your first campaign',
    copy: (p) => `${(p.optedIn || 0).toLocaleString()} contacts opted in to hear from you and no campaign is running yet. A drip converting just 2% each quarter ≈ ${'{v}'}.`,
  },
  campaign_new_segment: {
    icon: Megaphone,
    title: 'Untapped audience',
    copy: (p) => `Your "${p.segmentName}" segment has ${(p.count || 0).toLocaleString()} opted-in contacts that no campaign is touching. A dedicated drip converting 3% quarterly ≈ ${'{v}'}.`,
  },
  campaign_scale: {
    icon: TrendingUp,
    title: 'Scale what works',
    copy: (p) => `Your campaigns have earned ${(p.clicks || 0).toLocaleString()} clicks from ${(p.sent || 0).toLocaleString()} sends. Raising daily budgets puts your message in front of more of your proven audience ≈ ${'{v}'}.`,
  },
};

// Which insight kinds map to a pre-filled campaign (used on the Home tab cards)
export const CAMPAIGN_LINK_FOR_KIND = {
  repeat_rate: { goal: 'win_back', segment: 'lapsed', label: 'Create a win-back campaign' },
  load_factor: { goal: 'promote', segment: 'opted_in', label: 'Fill seats with a campaign' },
  midweek_gap: { goal: 'promote', segment: 'opted_in', label: 'Promote midweek with a campaign' },
  funnel_recovery: { goal: 'promote', segment: 'opted_in', label: 'Re-engage with a campaign' },
  traffic_conversion: { goal: 'promote', segment: 'opted_in', label: 'Bring them back with a campaign' },
  audience_activation: { goal: 'announce', segment: 'opted_in', label: 'Campaign this audience' },
  riders_conversion: { goal: 'promote', segment: 'riders', label: 'Campaign riders & guests' },
};

export const TIP_COPY = {
  low_earner: (p, fm) => `${p.assetName} earned just ${fm(p.revenue)} this period — consider re-pricing it, assigning it to more services, or promoting it on quieter days.`,
  fill_seats: (p) => `Your fleet sails at ${p.loadFactor}% of seat capacity — promote group bookings and per-seat add-ons to fill empty spots.`,
  addon_attach: (p) => `Only ${p.rate}% of bookings include an add-on — surfacing 2–3 add-ons at checkout typically lifts order value 5–15%.`,
  concentration: (p) => `${p.serviceName} drives ${p.share}% of your revenue — building a second flagship service would de-risk your season.`,
  lead_marketing: (p) => `Customers commit a median of ${p.days} days out — time your promos at least ${Math.ceil(p.days) + 3} days before the dates you want to fill.`,
  peak_slot: (p) => `Most trips depart around ${p.peakHour} — an extra sunset or early slot on peak days can capture overflow demand.`,
  winback: (p) => `Run a post-trip win-back campaign with a small return offer — your repeat rate is ${p.rate}%, so the upside is real.`,
  vip: (p, fm) => `Your top 10 customers spent ${fm(p.topSpend)} — a VIP early-access list keeps them coming back to you.`,
  holiday_pricing: (p) => `${p.holiday} is ${p.daysOut <= 1 ? 'almost here' : `${p.daysOut} days out`} — peak dates can carry 10–20% premium pricing or a holiday package. Set those rates now, before the window books up.`,
  funnel_top_service: (p) => `${p.serviceName} pulls the most opens (${p.views}) — its main photo and display pricing clearly work. Mirror that style on your quieter services.`,
  funnel_worst_service: (p) => `${p.serviceName} converts at ${p.convPct}% vs a ${p.medianPct}% median — try a new main photo, clearer display pricing, or fewer required add-ons.`,
  funnel_mobile_gap: (p) => `Mobile sessions check out at ${p.mobilePct}% vs ${p.desktopPct}% on desktop — walk the widget on a phone; every extra tap costs bookings.`,
  funnel_low_traffic: (p) => `Only ${p.views} widget sessions this period — the funnel needs traffic before it can teach you much. Link the widget from your top pages and socials.`,
  web_top_referrer: (p) => `${p.source} sends you the most visits (${p.sessions}) — double down there before spending anywhere else.`,
  web_utm: () => `No campaign tags detected in your traffic — add UTM tags (utm_source, utm_campaign) to your ad and social links so you can see exactly which ones pay.`,
  web_page_depth: (p) => `Visitors view ~${p.pages} pages per session — put a bold Book Now button or the embedded widget on your most-visited pages to shorten the path.`,
  aud_riders: (p) => `${p.riders} waiver signers have been on the water with you but never booked their own trip — a first-timer offer is the natural next touch.`,
  aud_optin: (p) => `${p.optInRate}% of your contacts are opted in so far — every signed waiver grows this list automatically. It's your zero-cost marketing channel.`,
  aud_lapsed: (p) => `${p.lapsed} past purchasers have gone quiet for 6+ months — a win-back offer to that segment usually outperforms any cold ad.`,
  aud_export: () => `Export your Top spenders segment into Meta as a Custom Audience and build a lookalike — it targets people who resemble your proven buyers.`,
  camp_optin_growth: (p) => `${(p.optedIn || 0).toLocaleString()} contacts are opted in today — every signed waiver and shared ticket link grows this list automatically, at zero cost.`,
  camp_test: () => `Send yourself a test email before launching — check the hero image, headline and button on your phone first.`,
  camp_frequency: () => `One message per contact per week is the sweet spot — the frequency cap protects your audience (and your credits) across all campaigns.`,
};

export const ACTION_COPY = {
  fleet_expansion: () => 'Charter or lease a similar vessel for peak season first — prove the demand before you buy.',
  pricing_power: (p) => `Raise ${p.serviceName} by ${p.pct}% for new bookings and watch conversion for 2–3 weeks — roll it back if it dips.`,
  midweek_gap: () => 'Run lower-priced midweek trips or a weekday-only promo code to pull demand off the weekend.',
  repeat_rate: () => 'Export past customers from your BookingFlows CRM and send a win-back offer with a small discount.',
  load_factor: () => 'Promote group bookings and per-seat add-ons so every departure carries more guests.',
  funnel_recovery: (p) => `Open the "${p.toLabel}" step in your widget and remove one point of friction — fewer fields, clearer pricing, or a more obvious next button.`,
  traffic_conversion: () => 'Put the booking widget (or a bold Book Now button) on your highest-traffic pages — visitors book where the button is.',
  audience_activation: () => 'Export your Marketing opt-ins segment from Audiences and send one simple offer email this month — measure it, then repeat quarterly.',
  riders_conversion: () => 'Export the Riders & guests segment and send a "book your own trip" intro offer — they already know the experience.',
  campaign_start: () => 'Hit "New campaign", pick your Marketing opt-ins, and let AI draft the message — you only pay per message sent.',
  campaign_new_segment: (p) => `Create a campaign targeting "${p.segmentName}" — AI drafts the copy and every send is tracked through to bookings.`,
  campaign_scale: () => 'Bump the daily budget on your best-performing campaign — the weekly frequency cap still protects your audience.',
};

const FunnelVisual = ({ visual }) => {
  const max = Math.max(...visual.steps.map((s) => Math.max(s.count, s.projected || 0)), 1);
  return (
    <div className="space-y-2.5" data-testid="ai-visual-funnel">
      {visual.steps.map((s) => {
        const w = Math.max(2, (s.count / max) * 100);
        const extra = s.projected && s.projected > s.count ? ((s.projected - s.count) / max) * 100 : 0;
        return (
          <div key={s.label}>
            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 mb-1">
              <span className="capitalize">{s.label}</span>
              <MonoNum className="font-extrabold text-slate-700">
                {s.count.toLocaleString()}
                {extra > 0 && <span className="text-[#60A5FA]"> → {s.projected.toLocaleString()}</span>}
              </MonoNum>
            </div>
            <div className="h-3.5 rounded-full bg-slate-100 overflow-hidden flex">
              <div className="h-full rounded-full bg-[#2563EB]" style={{ width: `${w}%` }} />
              {extra > 0 && <div className="h-full rounded-r-full bg-[#93C5FD]" style={{ width: `${extra}%` }} title="With this change" />}
            </div>
          </div>
        );
      })}
      {visual.steps.some((s) => s.projected > s.count) && (
        <div className="flex items-center gap-4 pt-1">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-[#2563EB]" /> Today</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-[#93C5FD]" /> With this change</span>
        </div>
      )}
    </div>
  );
};

const BarsVisual = ({ visual }) => {
  const max = Math.max(...visual.bars.map((b) => b.value), 1);
  return (
    <div className="space-y-2.5" data-testid="ai-visual-bars">
      {visual.bars.map((b) => (
        <div key={b.label}>
          <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 mb-1">
            <span>{b.label}</span>
            <MonoNum className={`font-extrabold ${b.projected ? 'text-[#60A5FA]' : 'text-slate-700'}`}>{b.value.toLocaleString()}</MonoNum>
          </div>
          <div className="h-3.5 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full ${b.projected ? 'bg-[#93C5FD]' : 'bg-[#2563EB]'}`}
              style={{ width: `${Math.max(2, (b.value / max) * 100)}%`, ...(b.projected ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.35) 0 6px, transparent 6px 12px)' } : {}) }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const DonutVisual = ({ visual }) => {
  const slices = (visual.slices || []).filter((s) => s.value > 0);
  return (
    <div className="flex items-center gap-5 flex-wrap sm:flex-nowrap" data-testid="ai-visual-donut">
      <div className="relative w-36 h-36 shrink-0 mx-auto sm:mx-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={slices} dataKey="value" nameKey="label" innerRadius={44} outerRadius={66} paddingAngle={2} strokeWidth={0}>
              {slices.map((_, i) => <Cell key={i} fill={BLUES[i % BLUES.length]} />)}
            </Pie>
            <Tooltip formatter={(v, name) => [v.toLocaleString(), name]} contentStyle={tooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <MonoNum className="text-lg font-extrabold text-slate-900">{(visual.total || 0).toLocaleString()}</MonoNum>
          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{visual.totalLabel || ''}</span>
        </div>
      </div>
      <div className="min-w-0 flex-1 space-y-1.5">
        {slices.map((s, i) => (
          <div key={s.label} className="flex items-center justify-between gap-3 text-xs">
            <span className="inline-flex items-center gap-2 font-semibold text-slate-600 min-w-0">
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: BLUES[i % BLUES.length] }} />
              <span className="truncate">{s.label}</span>
            </span>
            <MonoNum className="font-extrabold text-slate-800">{s.value.toLocaleString()}</MonoNum>
          </div>
        ))}
        {visual.highlight && (
          <div className="mt-2 pt-2 border-t border-slate-200/70 flex items-center justify-between gap-3 text-xs">
            <span className="font-bold text-[#2563EB]">{visual.highlight.label}</span>
            <MonoNum className="font-extrabold text-[#2563EB]">{visual.highlight.value.toLocaleString()} · {visual.highlight.pct}%</MonoNum>
          </div>
        )}
      </div>
    </div>
  );
};

const VisualBlock = ({ visual, title }) => (
  <div className="rounded-xl bg-slate-50/80 p-3 sm:p-4" data-testid="ai-suggestion-visual">
    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">{title}</div>
    {visual.type === 'funnel' && <FunnelVisual visual={visual} />}
    {visual.type === 'bars' && <BarsVisual visual={visual} />}
    {visual.type === 'donut' && <DonutVisual visual={visual} />}
  </div>
);

const VISUAL_TITLES = { funnel: 'Where sessions go — today vs with this change', bars: 'Visitors vs widget engagement', donut: 'Your audience today' };

export const AiSuggestion = ({ report, months }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { formatMoney } = useOperatorLocale();
  const navigate = useNavigate();
  const orgId = localStorage.getItem('organizationId');

  useEffect(() => { setData(null); }, [months]);

  const openModal = () => {
    setOpen(true);
    if (!data) {
      setLoading(true);
      apiV2.get(`/organizations/${orgId}/intelligence/suggestion/${report}?months=${months}`)
        .then((res) => setData(res.data))
        .catch(() => toast.error('Could not load the suggestion'))
        .finally(() => setLoading(false));
    }
  };

  const meta = data?.kind ? INSIGHT_META[data.kind] : null;
  const projected = data?.projectedAnnual ? `+${formatMoney(data.projectedAnnual)}/yr` : '';
  const chartData = (data?.series || []).map((s) => ({ ...s, month: s.month.slice(5) + '/' + s.month.slice(2, 4) }));
  const tips = (data?.tips || []).filter((t) => TIP_COPY[t.kind]);
  const hasContent = data && (data.kind || tips.length > 0 || data.visual);
  const cta = data?.campaign;

  const goCampaign = () => {
    setOpen(false);
    navigate(`/intelligence/campaigns?create=1&goal=${cta.goal || 'win_back'}&segment=${cta.segment || 'opted_in'}`);
  };

  return (
    <>
      <button
        data-testid={`${report}-ai-suggestion`}
        onClick={openModal}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#2563EB]/25 bg-[#EBF2FF]/70 px-4 py-2 text-xs font-bold text-[#2563EB] hover:bg-[#EBF2FF] transition-colors duration-150"
      >
        <Sparkles className="w-3.5 h-3.5" /> AI Suggestion
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-0" style={{ fontFamily: "'Manrope', 'Inter', sans-serif" }} data-testid={`${report}-ai-suggestion-modal`}>
          <div className="p-5 sm:p-7">
            {loading ? (
              <div className="py-12 flex flex-col items-center gap-3">
                <DialogTitle className="sr-only">AI Suggestion</DialogTitle>
                <Sparkles className="w-6 h-6 text-[#2563EB] animate-pulse" />
                <p className="text-sm font-semibold text-slate-400">Reading your data…</p>
              </div>
            ) : !hasContent ? (
              <div className="py-10 text-center">
                <div className="w-12 h-12 rounded-full bg-[#EBF2FF] flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-5 h-5 text-[#2563EB]" />
                </div>
                <DialogTitle className="font-extrabold text-slate-900 mb-1.5">Not enough data yet</DialogTitle>
                <p className="text-sm text-slate-500">Keep taking bookings — suggestions unlock as your data grows.</p>
              </div>
            ) : (
              <>
                {data.kind ? (
                  <>
                    <div className="flex items-start justify-between gap-3 sm:gap-4 mb-2 pr-8">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
                          {React.createElement(meta.icon, { className: 'w-[18px] h-[18px]', strokeWidth: 1.75 })}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#2563EB]">AI Suggestion</div>
                          <DialogTitle className="text-base sm:text-lg font-extrabold tracking-tight text-slate-900 truncate">{meta.title}</DialogTitle>
                        </div>
                      </div>
                      <MonoNum className="text-base sm:text-lg font-extrabold text-[#2563EB] whitespace-nowrap" data-testid={`${report}-ai-projected`}>{projected}</MonoNum>
                    </div>
                    <p className="text-sm text-slate-500 leading-relaxed mb-5">
                      {meta.copy(data.params, formatMoney).replace('{v}', projected)}
                    </p>

                    {data.visual ? (
                      <VisualBlock visual={data.visual} title={VISUAL_TITLES[data.visual.type] || 'Your data'} />
                    ) : (
                      <div className="rounded-xl bg-slate-50/80 p-3 sm:p-4">
                        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">Monthly revenue — today vs with this change</div>
                        <div className="h-44 sm:h-52">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                              <defs>
                                <linearGradient id="aiCurrent" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.16} />
                                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" vertical={false} />
                              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                              <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                              <Tooltip formatter={(v, name) => [formatMoney(v), name]} contentStyle={tooltipStyle} />
                              <Legend wrapperStyle={{ fontSize: 11 }} iconType="plainline" />
                              <Area type="monotone" dataKey="current" name="Today" stroke="#2563EB" strokeWidth={2} fill="url(#aiCurrent)" />
                              <Line type="monotone" dataKey="projected" name="With this change" stroke="#60A5FA" strokeWidth={2} strokeDasharray="6 5" dot={false} opacity={0.9} />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}

                    {ACTION_COPY[data.kind] && (
                      <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-[#EBF2FF]/70 border border-[#2563EB]/15 p-3.5" data-testid={`${report}-ai-action`}>
                        <span className="text-[#2563EB] font-extrabold text-sm shrink-0 mt-px">→</span>
                        <p className="text-sm text-slate-700 leading-relaxed">
                          <span className="font-bold text-[#2563EB]">One move:</span>{' '}
                          {ACTION_COPY[data.kind](data.params)}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-1 pr-8">
                      <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
                        <Sparkles className="w-[18px] h-[18px]" strokeWidth={1.75} />
                      </span>
                      <DialogTitle className="text-base sm:text-lg font-extrabold tracking-tight text-slate-900">AI Suggestions</DialogTitle>
                    </div>
                    {data.visual && (
                      <div className="mt-4">
                        <VisualBlock visual={data.visual} title={VISUAL_TITLES[data.visual.type] || 'Your data'} />
                      </div>
                    )}
                  </>
                )}

                {cta && (
                  <button onClick={goCampaign} data-testid={`${report}-ai-campaign-cta`}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#2563EB] text-white text-sm font-bold hover:bg-[#1D4ED8] transition-colors">
                    <Send className="w-4 h-4" /> {cta.label || 'Create this campaign'} <ArrowRight className="w-4 h-4" />
                  </button>
                )}

                {tips.length > 0 && (
                  <div className="mt-5" data-testid={`${report}-ai-tips`}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-3">More ways to improve</div>
                    <ul className="space-y-3">
                      {tips.map((t) => (
                        <li key={t.kind} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
                          <Sparkles className="w-3.5 h-3.5 text-[#2563EB] mt-0.5 shrink-0" />
                          <span>{TIP_COPY[t.kind](t.params, formatMoney)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-[11px] text-slate-400 mt-4">
                  Directional estimates from your own booking data only. Read-only — nothing in your account changes.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
